import React, { useEffect, useState } from 'react';
import {
  Button,
  Form,
  Input,
  Label,
  Modal,
  Pagination,
  Select,
  Table,
  TextArea,
} from 'semantic-ui-react';
import { API, showError, showSuccess } from '../helpers';
import { ITEMS_PER_PAGE } from '../constants';

const STATUS_OPTIONS = [
  { key: 'all', text: '全部状态', value: '' },
  { key: 'queued', text: '排队中（queued）', value: 'queued' },
  { key: 'running', text: '解析中（running）', value: 'running' },
  { key: 'success', text: '成功（success）', value: 'success' },
  { key: 'failed', text: '失败（failed）', value: 'failed' },
  { key: 'cancelled', text: '已取消（cancelled）', value: 'cancelled' },
  { key: 'expired', text: '已过期（expired）', value: 'expired' },
];

const STATUS_COLOR = {
  queued: 'blue',
  running: 'teal',
  success: 'green',
  failed: 'red',
  cancelled: 'grey',
  expired: 'orange',
};

const BILLING_TEXT = {
  precharged: '已预扣（待下发）',
  charged: '已计费',
  refunded: '已退还',
};

const OcrTasksTable = () => {
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [status, setStatus] = useState('');
  const [nodeId, setNodeId] = useState('');
  const [keyword, setKeyword] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const loadTasks = async (page = 1, st = status, nd = nodeId, kw = keyword) => {
    setLoading(true);
    let url = `/api/ocr_task/?p=${page - 1}`;
    if (st) {
      url += `&status=${encodeURIComponent(st)}`;
    }
    if (nd) {
      url += `&node_id=${encodeURIComponent(nd)}`;
    }
    if (kw) {
      url += `&keyword=${encodeURIComponent(kw)}`;
    }
    const res = await API.get(url);
    const { success, message, data, total: count } = res.data;
    if (success) {
      setTasks(data || []);
      setTotal(count || 0);
      setActivePage(page);
    } else {
      showError(message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadTasks(1).catch((reason) => showError(reason));
  }, []);

  const openDetail = async (task) => {
    const res = await API.get(`/api/ocr_task/${task.id}`);
    const { success, message, data } = res.data;
    if (success) {
      setDetail(data);
      setDetailOpen(true);
    } else {
      showError(message);
    }
  };

  const retry = async (task) => {
    if (!window.confirm(`确定重新入队任务 ${task.task_id} 吗？`)) {
      return;
    }
    const res = await API.post(`/api/ocr_task/${task.id}/retry`);
    const { success, message } = res.data;
    if (success) {
      showSuccess(message || '已重新入队');
      await loadTasks(activePage);
    } else {
      showError(message);
    }
  };

  return (
    <>
      <Form>
        <Form.Group>
          <Form.Field width={3}>
            <Select
              options={STATUS_OPTIONS}
              value={status}
              onChange={(e, { value }) => {
                setStatus(value);
                loadTasks(1, value, nodeId, keyword).catch((reason) => showError(reason));
              }}
            />
          </Form.Field>
          <Form.Field width={4}>
            <Input
              placeholder='按节点 ID 筛选'
              value={nodeId}
              onChange={(e) => setNodeId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  loadTasks(1).catch((reason) => showError(reason));
                }
              }}
            />
          </Form.Field>
          <Form.Field width={5}>
            <Input
              placeholder='按 task_id / 文件名 / 令牌名搜索'
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  loadTasks(1).catch((reason) => showError(reason));
                }
              }}
            />
          </Form.Field>
          <Button
            type='button'
            loading={loading}
            onClick={() => loadTasks(1).catch((reason) => showError(reason))}
          >
            查询
          </Button>
        </Form.Group>
      </Form>
      <Table basic={'very'} compact size='small'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>ID</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>任务 ID</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>状态</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>文件</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>令牌</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>节点</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>优先级 / 预取</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>尝试</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>计费</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>页数 / 耗时</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>错误</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>入队时间</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>操作</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {tasks.map((task) => (
            <Table.Row key={task.id}>
              <Table.Cell>{task.id}</Table.Cell>
              <Table.Cell>
                <code style={{ fontSize: '12px' }}>{task.task_id}</code>
              </Table.Cell>
              <Table.Cell>
                <Label basic color={STATUS_COLOR[task.status] || 'grey'}>
                  {task.status}
                </Label>
                {task.cache_hit ? <Label basic color='purple'>缓存</Label> : null}
              </Table.Cell>
              <Table.Cell style={{ maxWidth: 200 }}>
                <div style={{ fontSize: '12px', wordBreak: 'break-all' }}>
                  {task.file_name || '-'}
                  <br />
                  {task.file_type || '-'}
                  {task.file_size ? ` / ${Math.round(task.file_size / 1024)}KB` : ''}
                </div>
              </Table.Cell>
              <Table.Cell style={{ fontSize: '12px' }}>
                {task.token_name || task.token_id}
              </Table.Cell>
              <Table.Cell style={{ fontSize: '12px' }}>
                {task.node_id || '-'}
              </Table.Cell>
              <Table.Cell>
                <Label basic color={task.prefetch ? 'blue' : 'grey'}>
                  {task.prefetch ? '预取' : '正式'} / P{task.priority}
                </Label>
              </Table.Cell>
              <Table.Cell>
                {task.attempt}/{task.max_attempts}
              </Table.Cell>
              <Table.Cell style={{ fontSize: '12px' }}>
                {BILLING_TEXT[task.billing_status] || task.billing_status || '-'}
              </Table.Cell>
              <Table.Cell style={{ fontSize: '12px' }}>
                {task.pages || '-'} / {task.elapsed_ms ? `${task.elapsed_ms}ms` : '-'}
              </Table.Cell>
              <Table.Cell style={{ maxWidth: 180 }}>
                <div style={{ fontSize: '12px', color: '#c0392b', wordBreak: 'break-all' }}>
                  {task.error_code ? `${task.error_code}: ${task.error_message || ''}` : '-'}
                </div>
              </Table.Cell>
              <Table.Cell style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                {task.queued_at || task.created_at || '-'}
              </Table.Cell>
              <Table.Cell style={{ whiteSpace: 'nowrap' }}>
                <Button size='mini' basic onClick={() => openDetail(task)}>
                  详情
                </Button>
                {['failed', 'cancelled', 'expired'].includes(task.status) ? (
                  <Button size='mini' basic color='green' onClick={() => retry(task)}>
                    重新入队
                  </Button>
                ) : null}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan='13'>
              <Pagination
                floated='right'
                activePage={activePage}
                onPageChange={(e, { activePage: page }) =>
                  loadTasks(page).catch((reason) => showError(reason))
                }
                size='small'
                siblingRange={1}
                totalPages={Math.max(1, Math.ceil(total / ITEMS_PER_PAGE))}
              />
              <span style={{ color: '#8a919f' }}>
                共 {total} 条任务（成功结果保留 7 天；md 缓存 24h；失败/取消/过期自动退还预扣）
              </span>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>

      <Modal open={detailOpen} onClose={() => setDetailOpen(false)} size='large'>
        <Modal.Header>任务详情：{detail ? detail.task_id : ''}</Modal.Header>
        <Modal.Content scrolling>
          {detail ? (
            <Form>
              <Form.Group widths='equal'>
                <Form.Field>
                  <label>状态</label>
                  <Input readOnly value={detail.status} />
                </Form.Field>
                <Form.Field>
                  <label>计费状态</label>
                  <Input
                    readOnly
                    value={`${BILLING_TEXT[detail.billing_status] || detail.billing_status}（预扣 ${detail.pre_consumed_quota}）`}
                  />
                </Form.Field>
                <Form.Field>
                  <label>节点 / 租约</label>
                  <Input
                    readOnly
                    value={`${detail.node_id || '-'} / ${detail.lease_id || '-'}`}
                  />
                </Form.Field>
                <Form.Field>
                  <label>尝试次数</label>
                  <Input readOnly value={`${detail.attempt}/${detail.max_attempts}`} />
                </Form.Field>
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Field>
                  <label>令牌</label>
                  <Input readOnly value={`${detail.token_name || '-'}（#${detail.token_id}）`} />
                </Form.Field>
                <Form.Field>
                  <label>优先级 / 预取 / 缓存</label>
                  <Input
                    readOnly
                    value={`P${detail.priority} / ${detail.prefetch ? '预取' : '正式'} / ${detail.cache_hit ? '缓存命中' : '否'}`}
                  />
                </Form.Field>
                <Form.Field>
                  <label>引擎 / 设备 / 页数 / 耗时</label>
                  <Input
                    readOnly
                    value={`${detail.engine || '-'} / ${detail.device || '-'} / ${detail.pages || '-'} / ${detail.elapsed_ms || '-'}ms`}
                  />
                </Form.Field>
                <Form.Field>
                  <label>OC 来源 / 字符数</label>
                  <Input readOnly value={`${detail.ocr_source || '-'} / ${detail.char_count || '-'}`} />
                </Form.Field>
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Field>
                  <label>入队 / 认领 / 完成</label>
                  <Input
                    readOnly
                    value={`${detail.queued_at || '-'} → ${detail.claimed_at || '-'} → ${detail.finished_at || '-'}`}
                  />
                </Form.Field>
                <Form.Field>
                  <label>错误</label>
                  <Input
                    readOnly
                    value={detail.error_code ? `${detail.error_code}: ${detail.error_message}` : '-'}
                  />
                </Form.Field>
              </Form.Group>
              <Form.Field>
                <label>文件地址（file_url，客户侧公网地址）</label>
                <TextArea readOnly rows={2} value={detail.file_url || ''} />
              </Form.Field>
              <Form.Field>
                <label>sha256 / 参数</label>
                <Input
                  readOnly
                  value={`${detail.file_sha256 || '-'} / ${detail.params_json || '-'}`}
                />
              </Form.Field>
              <Form.Field>
                <label>OCR 结果（ocr_md）</label>
                <TextArea readOnly rows={14} value={detail.ocr_md || '（尚未产出）'} />
              </Form.Field>
            </Form>
          ) : null}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setDetailOpen(false)}>关闭</Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default OcrTasksTable;
